import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signup } from "../../services/api";
import {
  MessageSquare,
  Zap,
  TrendingUp,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import "./SignupPage.css";




    


const SignupPage = () => {
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [plan, setPlan] = useState("starter");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [signupMode, setSignupMode] = useState("trial");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Comprehensive validation
    if (!businessName.trim()) {
      setError("Business name is required.");
      return;
    }

    if (!email.trim()) {
      setError("Email address is required.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!phone.trim()) {
      setError("Phone number is required.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    try {
      await signup(businessName, email, phone, password, {
        plan,
        billing_cycle: billingCycle,
        signup_mode: signupMode,
      });
      navigate(signupMode === "trial" ? "/main" : "/billing");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setError("An account with this email already exists.");
      } else if (detail) {
        setError(typeof detail === "string" ? detail : "Signup failed.");
      } else {
        setError("Signup failed. Please check your details and try again.");
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <div className="signup-page">
      {/* ===== LEFT PANEL ===== */}
      <div className="left-panel">
        <div className="left-inner">
          {/* Brand Section */}
          <div className="brand">
            <img src="/logo.png" alt="Blastmate Logo" />
            <span>Blastmate</span>
          </div>

          {/* Main Headline */}
          <h1 className="headline">
            Turn WhatsApp into your <span className="highlight">growth engine</span>
          </h1>

          {/* Tagline */}
          <p className="tagline">
            Blastmate gives businesses a blazing‑fast WhatsApp inbox, smart
            automations, and one‑click broadcast campaigns — all in one beautiful
            dashboard.
          </p>

          {/* Key Features */}
          <div className="highlights">
            <div className="highlight-item">
              <div className="highlight-icon">
                <MessageSquare size={16} strokeWidth={2.2} />
              </div>
              <span>Unified inbox for all WhatsApp conversations</span>
            </div>
            <div className="highlight-item">
              <div className="highlight-icon">
                <Zap size={16} strokeWidth={2.2} />
              </div>
              <span>Smart automations — auto‑reply, route & tag</span>
            </div>
            <div className="highlight-item">
              <div className="highlight-icon">
                <TrendingUp size={16} strokeWidth={2.2} />
              </div>
              <span>Broadcast campaigns with real‑time tracking</span>
            </div>
            <div className="highlight-item">
              <div className="highlight-icon">
                <ShieldCheck size={16} strokeWidth={2.2} />
              </div>
              <span>Built for WhatsApp Business API compliance</span>
            </div>
          </div>

          {/* Trust/Social Proof */}
          <div className="social-proof">
            <CheckCircle2 size={14} strokeWidth={2.2} />
            <span>
              Plan‑based pricing • Secure Razorpay checkout • Cancel anytime
            </span>
          </div>
        </div>
      </div>

      {/* ===== RIGHT PANEL ===== */}
      <div className="right-panel">
        <div className="form-container">
          <h2 className="form-title" style={{ marginBottom: "24px" }}>Create your account</h2>
          <form className="form" onSubmit={handleSubmit}>

            {/* 4. User Information Fields */}
            <div className="field">
              <label htmlFor="businessName">Business Name</label>
              <input
                type="text"
                id="businessName"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                placeholder="My Awesome Shop"
              />
            </div>

            <div className="field">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@business.com"
              />
            </div>

            <div className="field">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="+91 98765 43210"
              />
            </div>

            {/* 5. Password Fields (Two Column on Desktop) */}
            <div className="row">
              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Min. 8 characters"
                />
              </div>
              <div className="field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Re‑enter password"
                />
              </div>
            </div>

            {/* 6. Error Message Display */}
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}

            {/* 7. Submit Button */}
            <button type="submit" disabled={loading} className="button-primary">
              {loading ? "Creating account…" : "Start 14-day free trial"}
            </button>
          </form>

          {/* 8. Footer Links */}
          <p className="footer-link">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>

          {/* 9. Legal Text */}
          <p className="legal">
            By signing up you agree to our{" "}
            <Link to="/terms-of-service">Terms of Service</Link> and{" "}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;